import React, { useState, useEffect } from "react";
import styles from "./AliasList.module.scss";
import { AliasData, isRandomAlias } from "../../../hooks/api/aliases";
import { ProfileData } from "../../../hooks/api/profile";
import { filterAliases } from "../../../functions/filterAliases";
import { CategoryFilter, SelectedFilters } from "./CategoryFilter";
import { UserData } from "../../../hooks/api/user";
import { RuntimeData } from "../../../hooks/api/types";
import { useLocalLabels } from "../../../hooks/localLabels";
import { AliasGenerationButton } from "./AliasGenerationButton";
import { SearchIcon } from "../../Icons";
import { useFlaggedAnchorLinks } from "../../../hooks/flaggedAnchorLinks";
import { useL10n } from "../../../hooks/l10n";
import { Localized } from "../../Localized";
import { VisuallyHidden } from "../../VisuallyHidden";
import { MaskCard } from "./MaskCard";
import { isFlagActive } from "../../../functions/waffle";

export type Props = {
  aliases: AliasData[];
  profile: ProfileData;
  user: UserData;
  runtimeData?: RuntimeData;
  onCreate: (
    options:
      | { mask_type: "random" }
      | { mask_type: "custom"; address: string; blockPromotionals: boolean },
  ) => void;
  onUpdate: (alias: AliasData, updatedFields: Partial<AliasData>) => void;
  onDelete: (alias: AliasData) => void;
  onboarding?: boolean;
  children?: React.ReactNode;
  setModalOpenedState?: (state: boolean) => void;
};

/**
 * Display a list of <Alias> cards, with the ability to filter them or create a new alias.
 */
export const AliasList = (props: Props) => {
  const l10n = useL10n();
  const [stringFilterInput, setStringFilterInput] = useState("");
  const [stringFilterVisible, setStringFilterVisible] = useState(false);
  const [resetCheckBoxes, setCheckboxes] = useState(false);
  const [categoryFilters, setCategoryFilters] = useState<SelectedFilters>({});
  const [localLabels, storeLocalLabel] = useLocalLabels();
  const [generatedAlias, setGeneratedAlias] = useState<AliasData | undefined>(
    undefined,
  );
  const { onboarding = false } = props;
  // When <AliasList> gets added to the page, if there's an anchor link in the
  // URL pointing to a mask, scroll to that mask:
  useFlaggedAnchorLinks(
    [],
    props.aliases.map((alias) => encodeURIComponent(alias.full_address)),
  );
  const [openAlias, setOpenAlias] = useState<AliasData | undefined>(
    // If the mask was focused on by an anchor link, expand that one on page load:
    props.aliases.find(
      (alias) =>
        alias.full_address ===
        decodeURIComponent(document.location.hash.substring(1)),
    ),
  );
  const [existingAliases, setExistingAliases] = useState<AliasData[]>(
    props.aliases,
  );

  useEffect(() => {
    if (props.aliases.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOpenAlias(undefined);
    } else {
      const existingAliasIds = existingAliases.map((alias) => alias.id);
      const newAliases = props.aliases.filter(
        (alias) => existingAliasIds.indexOf(alias.id) === -1,
      );
      if (newAliases.length !== 0) {
        setOpenAlias(newAliases[0]);
      }
    }

    setExistingAliases(props.aliases);
  }, [props.aliases, existingAliases]);

  if (props.aliases.length === 0) {
    return null;
  }

  const aliasesWithLocalLabels = props.aliases.map((alias) => {
    const aliasWithLocalLabel = { ...alias };
    if (
      alias.description.length === 0 &&
      props.profile.server_storage === false &&
      localLabels !== null
    ) {
      const localLabel = localLabels.find(
        (localLabel) =>
          localLabel.id === alias.id &&
          localLabel.mask_type === alias.mask_type,
      );
      if (localLabel !== undefined) {
        aliasWithLocalLabel.description = localLabel.description;
      }
    }
    return aliasWithLocalLabel;
  });

  const aliases = sortAliases(
    filterAliases(aliasesWithLocalLabels, {
      ...categoryFilters,
      string: stringFilterInput,
    }),
  );

  const findAliasDataFromPrefix = (
    aliasPrefix: string,
  ): AliasData | undefined => {
    return aliases.find((alias) => aliasPrefix === alias.address);
  };

  const aliasCards = aliases.map((alias) => {
    const onUpdate = (updatedFields: Partial<AliasData>) => {
      if (
        localLabels !== null &&
        typeof updatedFields.description === "string" &&
        props.profile.server_storage === false
      ) {
        storeLocalLabel(alias, updatedFields.description);
        delete updatedFields.description;
      }
      return props.onUpdate(alias, updatedFields);
    };

    const onChangeOpen = (isOpen: boolean) => {
      if (isOpen === true) {
        setOpenAlias(alias);
      } else if (openAlias !== undefined && openAlias.id === alias.id) {
        setOpenAlias(undefined);
      }
    };

    return (
      <li
        className={styles["alias-card-wrapper"]}
        key={alias.address + isRandomAlias(alias)}
        id={encodeURIComponent(alias.full_address)}
      >
        <MaskCard
          mask={alias}
          user={props.user}
          profile={props.profile}
          onUpdate={onUpdate}
          onDelete={() => props.onDelete(alias)}
          isOpen={
            openAlias !== undefined &&
            openAlias.id === alias.id &&
            openAlias.mask_type === alias.mask_type
          }
          onChangeOpen={onChangeOpen}
          showLabelEditor={props.profile.server_storage || localLabels !== null}
          runtimeData={props.runtimeData}
          isOnboarding={onboarding}
          copyAfterMaskGeneration={generatedAlias?.id === alias.id}
          setModalOpenedState={props.setModalOpenedState}
        >
          {props.children}
        </MaskCard>
      </li>
    );
  });

  // Filters enabled for all users, only blocked and forwarding show for non-premium
  const categoryFilter =
    props.profile.has_premium ||
    isFlagActive(props.runtimeData, "increased_free_mask_limit") ? (
      <div className={styles["category-filter"]}>
        <CategoryFilter
          onChange={setCategoryFilters}
          selectedFilters={categoryFilters}
          resetChecks={resetCheckBoxes}
          setCheckboxes={setCheckboxes}
          showPremiumFilters={props.profile.has_premium}
        />
      </div>
    ) : null;

  const emptyStateMessage =
    props.aliases.length > 0 && aliases.length === 0 ? (
      <Localized
        id="profile-filter-no-results"
        elems={{
          "clear-button": (
            <button
              onClick={() => {
                setCategoryFilters({});
                setCheckboxes(true);
                setStringFilterInput("");
              }}
              className={styles["clear-filters-button"]}
            />
          ),
        }}
      >
        <p className={styles["empty-state-message"]} />
      </Localized>
    ) : null;

  const handleOnChange = (value: string) => {
    // removes Unicode characters found within the range of 0080 to FFFF
    setStringFilterInput(value.replace(/[\u{0080}-\u{FFFF}]/gu, ""));
  };

  return (
    <section className={styles["alias-list-container"]}>
      {isFlagActive(props.runtimeData, "free_user_onboarding") &&
      onboarding ? null : (
        <div className={styles.controls}>
          <div
            className={`${styles["string-filter"]} ${
              stringFilterVisible ? styles["is-visible"] : ""
            }`}
          >
            <VisuallyHidden>
              <label htmlFor="stringFilter">
                {l10n.getString("profile-filter-search-placeholder-2")}
              </label>
            </VisuallyHidden>
            <input
              value={stringFilterInput}
              onChange={(e) => handleOnChange(e.target.value)}
              type="search"
              name="stringFilter"
              id="stringFilter"
              placeholder={l10n.getString(
                "profile-filter-search-placeholder-2",
              )}
            />
            <span className={styles["match-count"]}>
              {aliases.length}/{props.aliases.length}
            </span>
          </div>
          <button
            onClick={() => setStringFilterVisible(!stringFilterVisible)}
            title={l10n.getString("profile-filter-search-placeholder-2")}
            className={`${styles["string-filter-toggle"]} ${
              stringFilterVisible ? styles["active"] : ""
            }`}
          >
            <SearchIcon
              alt={l10n.getString("profile-filter-search-placeholder-2")}
              width={20}
              height={20}
            />
          </button>
          {categoryFilter}
          <div className={styles["new-alias-button"]}>
            <AliasGenerationButton
              aliases={props.aliases}
              profile={props.profile}
              runtimeData={props.runtimeData}
              onCreate={props.onCreate}
              onUpdate={props.onUpdate}
              findAliasDataFromPrefix={findAliasDataFromPrefix}
              setGeneratedAlias={setGeneratedAlias}
            />
          </div>
        </div>
      )}
      <ul data-testid="alias-list">
        {isFlagActive(props.runtimeData, "free_user_onboarding") && onboarding
          ? aliasCards[0]
          : aliasCards}
      </ul>
      {emptyStateMessage}
    </section>
  );
};

function sortAliases(aliases: AliasData[]): AliasData[] {
  const aliasDataCopy = aliases.slice();
  aliasDataCopy.sort((aliasA, aliasB) => {
    // `Date.parse` can be inconsistent,
    // but should be fairly reliable in parsing ISO 8601 strings
    // (though if Temporal ever gets accepted by TC39, we should switch to that):
    const aliasATimestamp = Date.parse(aliasA.created_at);
    const aliasBTimestamp = Date.parse(aliasB.created_at);
    return aliasBTimestamp - aliasATimestamp;
  });
  return aliasDataCopy;
}
